import { ActionIcon, Card, Group, Stack } from "@mantine/core";

import { IconCircleMinus, IconCirclePlus } from "@tabler/icons-react";

import { TextWrapper } from "@/components/TextWrapper";

import { FLAG_CATEGORY_CODINGS } from "../constants/flags";
import { ROW_ICON_SIZE } from "../constants/ui";
import { ClientFlag } from "../interfaces/client-flags";
import { PracticeFlag } from "../interfaces/flags";

import FlagDeprecatedBadge from "./FlagDeprecatedBadge";
import FlagPriorityDot from "./FlagPriorityDot";

/**
 * The drawer renders three kinds of row, differing in what they are and in what their control
 * does.
 *
 * `add` is a library entry with a `+` that stages it. `staged` is one already staged, whose `−`
 * takes it back out of the list — nothing was written, so there is nothing to remove from the
 * chart. `applied` is a flag the chart carries, which can be deprecated, and whose `−` opens
 * the drawer's Remove Flag step. A staged row never reaches the removal service, since its id
 * is a library entry's id.
 *
 * An applied row without `onRemove` has no control, for a viewer whose role cannot write a
 * Flag.
 */
type ClientFlagDrawerRowProps =
  | { variant: "add"; flag: PracticeFlag; onAdd: (libraryFlagId: string) => void }
  | { variant: "staged"; flag: PracticeFlag; onUnstage: (libraryFlagId: string) => void }
  | { variant: "applied"; flag: ClientFlag; onRemove?: (flag: ClientFlag) => void };

/** One card in either drawer section: priority dot, name, category beneath, control on the right. */
export default function ClientFlagDrawerRow(props: ClientFlagDrawerRowProps) {
  const { flag, variant } = props;
  const isDeprecated = variant === "applied" && props.flag.isDeprecated;
  const handleRemove = getRemoveHandler(props);

  return (
    <Card
      p="md"
      bg="white"
      withBorder
      radius="sm"
      data-testid={`client-flag-drawer-row-${flag.id}`}
      style={{ borderColor: "var(--mantine-color-cream-4)" }}
    >
      <Group gap="sm" wrap="nowrap" justify="space-between">
        <Group gap="sm" wrap="nowrap">
          <FlagPriorityDot priority={flag.priority} isDeprecated={isDeprecated} />
          <Stack gap={0}>
            <TextWrapper variant="bold">{flag.name}</TextWrapper>
            {flag.category && (
              <TextWrapper size="sm" c="gray.7">
                {FLAG_CATEGORY_CODINGS[flag.category].display}
              </TextWrapper>
            )}
          </Stack>
        </Group>
        {isDeprecated && <FlagDeprecatedBadge />}
        {variant === "add" && (
          <ActionIcon
            color="dark.9"
            variant="subtle"
            aria-label={`Add ${flag.name}`}
            onClick={() => props.onAdd(flag.id)}
            data-testid={`client-flag-add-${flag.id}`}
          >
            <IconCirclePlus size={ROW_ICON_SIZE} />
          </ActionIcon>
        )}
        {handleRemove && (
          <ActionIcon
            variant="subtle"
            color="burgundy.9"
            onClick={handleRemove}
            aria-label={`Remove ${flag.name}`}
            data-testid={`client-flag-remove-${flag.id}`}
          >
            <IconCircleMinus size={ROW_ICON_SIZE} />
          </ActionIcon>
        )}
      </Group>
    </Card>
  );
}

/**
 * The row's `−` action: a staged row unstages itself, and an applied row hands itself to `onRemove`.
 *
 * @param props - The row's props.
 * @returns The click handler, or `undefined` when the row has no `−` — an `add` row, or an applied
 * row without `onRemove`.
 */
function getRemoveHandler(props: ClientFlagDrawerRowProps) {
  if (props.variant === "staged") {
    const { flag, onUnstage } = props;
    return () => onUnstage(flag.id);
  }

  if (props.variant === "applied" && props.onRemove) {
    const { flag, onRemove } = props;
    return () => onRemove(flag);
  }

  return undefined;
}
